// src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  Divider,
  Grid,
  Paper,
  Button,
  Tooltip,
  Collapse,
  Menu,
  MenuItem,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HomeIcon from '@mui/icons-material/Home';
import SaveIcon from '@mui/icons-material/Save';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import MenuIcon from '@mui/icons-material/Menu';
import FileUpload from './FileUpload';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, getDoc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import MonacoEditorComponent from './MonacoEditorComponent';
import QueryInput from './QueryInput/QueryInput';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import '../App.css'; // Import the CSS file
import { SnackPreview } from './snackPreview';
import { v4 as uuidv4 } from 'uuid';
import BrowserTerminal from './BrowserTerminal';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TerminalIcon from '@mui/icons-material/Terminal';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [energy, setEnergy] = useState(0);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState('');
  const [code, setCode] = useState('');
  const [initialFileSelected, setInitialFileSelected] = useState(false);
  const [previousCode, setPreviousCode] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isApply, setIsApply] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextFile, setContextFile] = useState(null);
  const [existingDependencies, setExistingDependencies] = useState([]);
  const [localInit, setLocalInit] = useState(true);

  const location = useLocation();
  const {
    projectId,
    files: initialFiles = [],
    dependencies: initialDependencies = [],
    projectName = 'No Project Loaded',
    description,
    projectType,
    init
  } = location.state || {};

  // Terminal open/close state
  const [terminalOpen, setTerminalOpen] = useState(false);

  // --- Toggle Terminal ---
  const toggleTerminal = () => {
    setTerminalOpen((prev) => !prev);
  };

  useEffect(() => {
    setLocalInit(init);
    const fetchFilesAndDependenciesFromFirestore = async () => {
      if (!projectId || !currentUser) return;

      try {
        const filesCollectionRef = collection(
          db,
          `users/${currentUser.uid}/projects/${projectId}/files`
        );
        const filesSnapshot = await getDocs(filesCollectionRef);
        const filesList = await Promise.all(
          filesSnapshot.docs.map(async (docSnap) => {
            const { name, url } = docSnap.data();
            const response = await fetch(url);
            const content = await response.text();
            const file = new File([content], name, { type: 'text/plain' });
            file.id = docSnap.id; // Add the id as a custom property
            return file;
          })
        );
        setFiles(filesList);

        // Fetch dependencies
        const projectDocRef = doc(db, `users/${currentUser.uid}/projects`, projectId);
        const projectDoc = await getDoc(projectDocRef);
        if (projectDoc.exists()) {
          setExistingDependencies(projectDoc.data().dependencies || []);
        }
      } catch (error) {
        console.error('Error fetching files and dependencies:', error);
      }
    };

    fetchFilesAndDependenciesFromFirestore();
  }, [projectId, currentUser, init]);

  useEffect(() => {
    // Prevent scrolling in the background
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    // Cleanup function to reset overflow
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const saveAllFilesToFirebase = async (filesToSave) => {
    // If no argument is provided, default to all existing files in state
    const filesArray = filesToSave || files;

    if (filesArray.length === 0) {
      console.warn('No files to save.');
      return;
    }

    try {
      await Promise.all(filesArray.map((file) => saveSingleFileToFirebase(file)));
      await saveDependenciesToFirebase();
      //console.log('All files and dependencies saved successfully.');
    } catch (error) {
      console.error('Error saving files and dependencies:', error);
    }
  };

  const saveSingleFileToFirebase = async (file) => {
    try {
      const fileId = file.id;
      const storageRef = ref(
        storage,
        `users/${currentUser.uid}/projects/${projectId}/files/${fileId}`
      );

      // Upload file to Firebase Storage
      await uploadBytes(storageRef, file);
      const fileURL = await getDownloadURL(storageRef);

      // Save file metadata to Firestore
      const fileDocRef = doc(
        db,
        `users/${currentUser.uid}/projects/${projectId}/files`,
        fileId
      );
      await setDoc(fileDocRef, {
        name: file.name,
        url: fileURL,
      });

      //console.log('File saved successfully:', file.name);
    } catch (error) {
      console.error('Error saving file:', error);
    }
  };

  const saveDependenciesToFirebase = async () => {
    try {
      const projectDocRef = doc(db, `users/${currentUser.uid}/projects`, projectId);
      await setDoc(
        projectDocRef,
        {
          dependencies: existingDependencies,
        },
        { merge: true }
      );
      //console.log('Dependencies saved successfully.');
    } catch (error) {
      console.error('Error saving dependencies:', error);
    }
  };

  useEffect(() => {
    if (existingDependencies.length > 0) {
      saveDependenciesToFirebase();
    }
  }, [existingDependencies]);

  const addDependencies = (newDependencies) => {
    setExistingDependencies((prevDependencies) => {
      const dependenciesArray = Array.isArray(newDependencies)
        ? newDependencies
        : [newDependencies];
      // Create a new set to avoid duplicates
      const updatedDependencies = new Set([...prevDependencies, ...dependenciesArray]);
      return Array.from(updatedDependencies); // Convert back to array
    });
    //console.log(existingDependencies);
  };

  useEffect(() => {
    const fetchEnergy = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setEnergy(Math.round(userDoc.data().energy || 0));
        }
      } catch (err) {
        console.error('Error fetching energy:', err);
      }
    };

    fetchEnergy();
  }, [currentUser]);

  const handleFilesSelected = (selectedFiles) => {
    const filesWithIds = selectedFiles.map((file) => {
      file.id = uuidv4(); // Assign a unique id to each file
      return file;
    });
    setFiles((prevFiles) => [...prevFiles, ...filesWithIds]);
    saveAllFilesToFirebase(filesWithIds);
  };

  const navigateToProfile = () => {
    navigate('/profile');
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file.name);

    // Read the file content
    const reader = new FileReader();
    reader.onload = () => {
      setCode(reader.result);
    };
    reader.readAsText(file);
  };

  const toggleDrawer = () => {
    setDrawerOpen((prevOpen) => !prevOpen);
  };

  const saveFile = (newCode) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (file.name === selectedFile) {
          // Create a new file object with updated content and preserve the file id
          const updatedFile = new File([newCode], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          updatedFile.id = file.id; // Preserve the file id
          saveSingleFileToFirebase(updatedFile);
          return updatedFile;
        }
        return file;
      })
    );
    setCode(newCode);
  };

  const createFile = (name, content, mimeType) => {
    const fileName = name.split('/').pop();
    const fileExists = files.some((file) => file.name === fileName);

    if (!fileExists) {
      const newFile = new File([content], fileName, {
        type: mimeType,
        lastModified: Date.now(),
      });
      newFile.id = uuidv4(); // Assign a unique id
      setFiles((prevFiles) => [...prevFiles, newFile]);
      setSelectedFile(fileName);
      setCode(content);
      saveSingleFileToFirebase(newFile);
    } else {
      console.warn(`File with name "${fileName}" already exists.`);
    }
  };

  const removeFile = async (fileName) => {
    const fileToRemove = files.find((file) => file.name === fileName);
    if (!fileToRemove) return;

    try {
      // Delete from Firebase Storage
      const storageRef = ref(
        storage,
        `users/${currentUser.uid}/projects/${projectId}/files/${fileToRemove.id}`
      );
      await deleteObject(storageRef);

      // Delete metadata from Firestore
      const fileDocRef = doc(
        db,
        `users/${currentUser.uid}/projects/${projectId}/files`,
        fileToRemove.id
      );
      await deleteDoc(fileDocRef);

      //console.log('File deleted successfully:', fileName);
    } catch (error) {
      console.error('Error deleting file:', error);
    }

    // Update local state
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    if (selectedFile === fileName) {
      setSelectedFile('');
      setCode('');
    }
  };

  const handleContextMenu = (event, file) => {
    event.preventDefault();
    setContextFile(file);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
    setContextFile(null);
  };

  const handleDelete = () => {
    if (contextFile) {
      removeFile(contextFile.name);
    }
    handleClose();
  };

  const getMimeType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const mimeTypes = {
      txt: 'text/plain',
      js: 'application/javascript',
      json: 'application/json',
      html: 'text/html',
      css: 'text/css',
      py: 'text/x-python',
      java: 'text/x-java-source',
      c: 'text/x-c',
      cpp: 'text/x-c++src',
      ts: 'application/typescript',
      jsx: 'text/jsx',
      tsx: 'text/tsx',
      xml: 'application/xml',
      md: 'text/markdown',
      csv: 'text/csv',
      // Add more mappings as needed
    };
    return mimeTypes[extension] || 'application/octet-stream';
  };

  const createNewFile = () => {
    const newFileName = window.prompt('Enter the name for the new file:');
    if (newFileName) {
      const mimeType = getMimeType(newFileName);
      createFile(newFileName, '', mimeType);
    }
  };

  const getMonacoLanguage = (mimeType) => {
    const mimeToLanguageMap = {
      'text/plain': 'plaintext',
      'application/javascript': 'javascript',
      'application/json': 'json',
      'text/html': 'html',
      'text/css': 'css',
      'text/x-python': 'python',
      'text/x-java-source': 'java',
      'text/x-c': 'c',
      'text/x-c++src': 'cpp',
      'application/typescript': 'typescript',
      'text/jsx': 'javascript',
      'text/tsx': 'typescript',
      'application/xml': 'xml',
      'text/markdown': 'markdown',
      'text/csv': 'plaintext',
    };
    return mimeToLanguageMap[mimeType] || 'plaintext';
  };

  useEffect(() => {
    if (files.length > 0 && !initialFileSelected) {
      handleFileSelect(files[0]);
      setInitialFileSelected(true);
    }
  }, [files, initialFileSelected]);

  // --- Sidebar Content ---
  const drawer = (
    <div>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: [1],
        }}
      >
        <Typography variant="h6" noWrap>
          Files
        </Typography>
        <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Toolbar>
      <Divider />
      <Collapse in={drawerOpen}>
        <Box>
          <FileUpload
            userId={currentUser.uid}
            onFilesSelected={handleFilesSelected}
            newFile={createNewFile}
          />
        </Box>
        <List>
          {files.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
              No files uploaded.
            </Typography>
          ) : (
            files.map((file) => (
              <ListItem
                key={file.id || file.name}
                disablePadding
                onContextMenu={(event) => handleContextMenu(event, file)}
              >
                <ListItemButton
                  selected={file.name === selectedFile}
                  onClick={() => handleFileSelect(file)}
                >
                  <ListItemText primary={file.name} />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Collapse>
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        MenuListProps={{
          sx: {
            padding: 0,
            backgroundColor: 'transparent',
          },
        }}
      >
        <MenuItem
          onClick={handleDelete}
          sx={{
            backgroundColor: '#1e1e1e',
            color: 'white',
            minWidth: '100px',
            padding: '4px 8px',
            '&:hover': {
              backgroundColor: '#333',
            },
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </div>
  );

  // --- Render ---
  return (
    <Box className="body-style">
      <CssBaseline />
      <AppBar position="fixed" className="appBar" elevation={1} color="#121212">
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{
              color: 'white',
              marginRight: '36px',
              ...(drawerOpen && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" className="chatEditorTitle">
            {selectedFile}
          </Typography>
          <Box display="flex" alignItems="center">
            <Tooltip title="Energy">
              <Box display="flex" alignItems="center" mr={2}>
                <BoltIcon sx={{ color: 'yellow' }} />
                <Typography
                  variant="subtitle1"
                  sx={{ ml: 0.5, color: 'white', fontSize: '1.25rem' }}
                >
                  {energy}
                </Typography>
              </Box>
            </Tooltip>
            {projectType === 'React Native (expo)' && (
              <Box sx={{ mr: 2 }}>
                <SnackPreview files={files} dependencies={existingDependencies} />
              </Box>
            )}

            {/* Terminal toggle button (Python projects only) */}
            {projectType === 'Python' && (
              <Box sx={{ mr: 2 }}>
                <Button
                  variant="contained"
                  onClick={toggleTerminal}
                  sx={{
                    backgroundColor: '#222',
                    color: 'white',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#444',
                    },
                  }}
                >
                  <TerminalIcon sx={{ color: 'white' }} />
                </Button>
              </Box>
            )}

            {/* Save All */}
            <Box sx={{ mr: 2 }}>
              <Button
                variant="contained"
                onClick={() => saveAllFilesToFirebase()}
                sx={{
                  backgroundColor: '#222',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#444',
                  },
                }}
              >
                <SaveIcon />
              </Button>
            </Box>

            {/* Profile */}
            <Box sx={{ mr: 2 }}>
              <Button
                variant="contained"
                onClick={navigateToProfile}
                sx={{
                  backgroundColor: '#222',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#444',
                  },
                }}
              >
                <AccountCircleIcon />
              </Button>
            </Box>

            {/* Go Home */}
            <Button
              variant="contained"
              onClick={() => navigate('/project-manager')}
              sx={{
                backgroundColor: '#222',
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#444',
                },
              }}
            >
              <HomeIcon />
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        classes={{ paper: 'drawerPaper' }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        className="mainContent"
        sx={{ flexGrow: 1, overflow: 'hidden', height: '100vh' }}
      >
        <Toolbar />
        <Grid container>
          <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '93vh', // Adjust to fit your layout
            }}
          >
            {/* Editor below the terminal */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <MonacoEditorComponent
                language={getMonacoLanguage(getMimeType(selectedFile))}
                value={code}
                previousValue={previousCode}
                saveFile={saveFile}
                theme="vs-dark"
                height="100%"
                options={{
                  fontSize: 14,
                  automaticLayout: true,
                  scrollbar: {
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8,
                  },
                }}
                isApply={isApply}
                onAccept={setIsApply}
                setCode={setCode}
              />
            </Box>
            {terminalOpen && (
              <Box
                sx={{
                  position: 'relative',
                  backgroundColor: '#1e1e1e',
                  color: 'white',
                  overflowY: 'auto',
                  resize: 'vertical',
                  height: '40vh',
                }}
              >
                {/* Draggable handle */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '10px',
                    cursor: 'row-resize',
                    backgroundColor: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '"..."',
                      color: 'white',
                      fontSize: '1rem',
                      paddingBottom: '8px',
                    },
                  }}
                  onMouseDown={(e) => {
                    const startY = e.clientY;
                    const startHeight = e.target.parentElement.offsetHeight;

                    const onMouseMove = (moveEvent) => {
                      const newHeight = startHeight + (startY - moveEvent.clientY);
                      e.target.parentElement.style.height = `${newHeight}px`;
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />
                <BrowserTerminal name={projectName} onClose={toggleTerminal} files={files} />
              </Box>
            )}
          </Box>
          </Grid>

          {/* Right Column: Query / AI Helper */}
          <Grid item xs={12} md={4}>
            <QueryInput
              userId={currentUser.uid}
              files={files}
              energy={energy}
              updateEnergy={setEnergy}
              setCode={setCode}
              isApply={isApply}
              setIsApply={setIsApply}
              setPreviousCode={setPreviousCode}
              setSelectedFile={setSelectedFile}
              selectedFile={selectedFile}
              createFile={createFile}
              handleFileSelect={handleFileSelect}
              addDependencies={addDependencies}
              localInit={localInit}
              description={description}
              setLocalInit={setLocalInit}
              initFiles={files}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
